import { createApp } from 'vue'
import { createPinia, storeToRefs } from 'pinia'
import { useMessageStack } from '@/stores/messagestack'
import { useModalStack } from '@/stores/modalstack'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import { ApiError } from '@/composables/api'
import { i18n } from '@/i18n'

import App from '@/app.vue'
import router from '@/router'
import ErrorConnectionModal from '@/components/errorConnectionModal.vue'

const app = createApp(App)

app.use(createPinia())
app.use(i18n)
app.use(router)

const messageStack = useMessageStack()
const modalStack = useModalStack()
const { user } = storeToRefs(useUserStore())
const { blockScreen } = storeToRefs(useUiStore())

app.config.errorHandler = errorHandler
app.mount('#app')

/**
 * Проверяет, является ли ошибка связанной с загрузкой модулей
 */
function isChunkLoadError(err) {
  const errStr = String(err)
  // Только ошибки загрузки JS-чанков. Обычный NetworkError от API (current_status и т.п.)
  // тоже TypeError + «fetch» — его сюда нельзя, иначе ложный «Ошибка загрузки».
  return (
    errStr.includes('Failed to fetch dynamically imported module') ||
    errStr.includes('Failed to load module script') ||
    errStr.includes('Importing a module script failed')
  )
}

/**
 * Показывает модальное окно с ошибкой связи
 */
function showConnectionErrorModal() {
  // Проверяем, не открыта ли уже модалка
  if (modalStack.stack.length > 0) {
    return
  }

  modalStack.push({
    component: ErrorConnectionModal,
    props: {
      headerText: i18n.global.t('common.loadError'),
      class: 'modal-sm',
      fitContent: true,
    },
  })
}

async function errorHandler(err) {
  let errMessage = String(err)

  console.error(err)

  // Обработка ошибок загрузки модулей
  if (isChunkLoadError(err)) {
    blockScreen.value = false
    showConnectionErrorModal()
    return
  }

  if (err instanceof ApiError) {
    if (err.code === 401) {
      user.value = undefined
      errMessage = i18n.global.t('common.sessionExpired')
    } else {
      // Показываем description из JSON ответа сервера, иначе — текст по коду статуса
      const statusLabel = i18n.global.t(`http.status.${err.status}`)
      errMessage = err.message || `${err.status} ${statusLabel}`
    }
  }

  const httpErrors = i18n.global.tm('http.errors')
  if (httpErrors && typeof httpErrors === 'object' && errMessage in httpErrors) {
    errMessage = httpErrors[errMessage]
  }

  blockScreen.value = false
  messageStack.push({ type: 'error', message: errMessage })
}

// Обработчик ошибок роутера
router.onError(err => {
  console.error('Router error:', err)
  if (isChunkLoadError(err)) {
    blockScreen.value = false
    showConnectionErrorModal()
  }
})

window.onerror = (m, u, l, c, e) => errorHandler(e || m)
window.onunhandledrejection = ({ reason }) => errorHandler(reason)
